/**
 * Login Page
 * Authentication with Google Sign-In and email fallback
 */

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { Input, Button } from '../../components';
import { useAuth } from '../../context/AuthContext';
import SEO from '../../components/common/SEO';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle } = useAuth();
  const from = location.state?.from?.pathname || location.state?.from || null;
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      setError('');
      try {
        const user = await loginWithGoogle(tokenResponse.access_token);
        navigate(from || `/${user.role}/dashboard`, { replace: true });
      } catch (err) {
        setError(err.message || 'Google sign-in failed. Please try again.');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      setError('Google sign-in was cancelled or failed. Please try again.');
    },
  });

  const handleGoogleSignIn = () => {
    setError('');
    googleLogin();
  };

  const handleSubmit = async (e) => { 
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const user = await login(formData.email, formData.password);
      navigate(from || `/${user.role}/dashboard`, { replace: true });
      return;
    } catch (err) {
      setError(err.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lh-login-page min-h-screen bg-gradient-to-br from-dark-50 via-white to-primary-50 flex items-center justify-center p-4">
      <SEO
        title="Login"
        description="Log in to your Lotus Arc Technologies account to access your courses, live classes, and learning dashboard."
        canonicalPath="/login"
        noIndex
      />
      <div className="lh-login__container w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="lh-login__logo flex items-center justify-center gap-2 mb-8">
          <img src="/lat-icon.png" alt="Lotus Arc Technologies" className="h-14 w-auto" />
          <span className="text-2xl font-bold text-dark-900">LAT</span>
        </Link>

        {/* Login Card */}
        <div className="lh-login__card bg-white rounded-2xl shadow-xl p-8">
          <div className="lh-login__header text-center mb-8">
            <h1 className="lh-login__title text-2xl font-bold text-dark-900 mb-2">
              Welcome Back!
            </h1>
            <p className="lh-login__subtitle text-dark-500">
              Sign in to continue your learning journey
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="lh-login__error bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}

          {/* Google Sign-In */}
          <button
            onClick={handleGoogleSignIn}
            disabled={loading || googleLoading}
            className="lh-login__google-btn w-full flex items-center justify-center gap-3 bg-white border-2 border-dark-200 hover:border-dark-300 text-dark-700 font-medium py-3 px-4 rounded-lg transition-colors mb-6 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {googleLoading ? (
              <div className="w-5 h-5 border-2 border-dark-300 border-t-primary-500 rounded-full animate-spin" />
            ) : (
              <svg className="lh-login__google-icon w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            )}
            <span className="lh-login__google-text">
              {googleLoading ? 'Signing in...' : 'Sign in with Google'}
            </span>
          </button>

          {/* Divider */}
          <div className="lh-login__divider flex items-center gap-4 mb-6">
            <div className="lh-login__divider-line flex-1 h-px bg-dark-200" />
            <span className="lh-login__divider-text text-sm text-dark-400">or continue with email</span>
            <div className="lh-login__divider-line flex-1 h-px bg-dark-200" />
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="lh-login__form space-y-4">
            <Input
              label="Email Address"
              type="email"
              name="email"
              placeholder="Enter Your Email"
              value={formData.email}
              onChange={handleChange}
              required
              className="lh-login__input lh-login__input--email"
              icon={
                <svg className="lh-login__input-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              }
            />

            <div className="lh-login__password-wrapper relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                required
                className="lh-login__input lh-login__input--password"
                icon={
                  <svg className="lh-login__input-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                }
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="lh-login__password-toggle absolute right-3 top-9 text-dark-400 hover:text-dark-600"
              >
                {showPassword ? (
                  <svg className="lh-login__password-icon lh-login__password-icon--hide w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="lh-login__password-icon lh-login__password-icon--show w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>

            <div className="lh-login__options flex items-center justify-between">
              <label className="lh-login__remember flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="lh-login__remember-checkbox w-4 h-4 text-primary-500 rounded focus:ring-primary-500" />
                <span className="lh-login__remember-text text-sm text-dark-600">Remember me</span>
              </label>
              <Link to="/forgot-password" className="lh-login__forgot text-sm text-primary-500 hover:text-primary-600 font-medium">
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              className="lh-login__submit mt-6"
            >
              Sign In
            </Button>
          </form>

          {/* Sign Up Link */}
          <p className="lh-login__signup-link text-center mt-6 text-dark-500">
            Don't have an account?{' '}
            <Link to="/signup" className="lh-login__signup-link-action text-primary-500 hover:text-primary-600 font-medium">
              Sign up free
            </Link>
          </p>
        </div>

        {/* Back to Home */}
        <div className="lh-login__back text-center mt-6">
          <Link to="/" className="lh-login__back-link text-dark-500 hover:text-dark-700 text-sm flex items-center justify-center gap-2">
            <svg className="lh-login__back-icon w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="lh-login__back-text">Back to Home</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
